import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/GlobalStyles';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input, Label, FormGroup, Select, TextArea } from './Input';

const CalendarContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px ${colors.shadow};
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }
`;

const MonthTitle = styled.h2`
  color: ${colors.primary};
  margin: 0;
`;

const NavigationButton = styled.button`
  background: ${colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s ease;

  &:hover {
    background: ${colors.primaryDark};
  }

  &:disabled {
    background: ${colors.textLight};
    cursor: not-allowed;
  }
`;

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const WeekDay = styled.div`
  padding: 10px;
  text-align: center;
  font-weight: bold;
  color: ${colors.primary};
  background: ${colors.secondary};
  font-size: 14px;

  @media (max-width: 768px) {
    padding: 6px 4px;
    font-size: 12px;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: ${colors.border};
`;

const CalendarDay = styled.div`
  min-height: 120px;
  padding: 8px;
  background: white;
  border: 1px solid ${colors.border};
  position: relative;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: ${colors.secondary};
  }

  ${props => props.isOtherMonth && `
    background: ${colors.background};
    color: ${colors.textLight};
  `}

  ${props => props.isToday && `
    background: ${colors.success};
    color: white;
    font-weight: bold;
  `}

  @media (max-width: 768px) {
    min-height: 80px;
    padding: 4px;
  }

  @media (max-width: 480px) {
    min-height: 60px;
    padding: 2px;
  }
`;

const DayNumber = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 2px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
    margin-bottom: 1px;
  }
`;

const ClassList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ClassItem = styled.div`
  background: ${props => {
    if (props.$isPast) return colors.textLight;
    return props.isRecurring ? colors.success : colors.primary;
  }};
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  cursor: pointer;
  opacity: ${props => props.$isPast ? 0.6 : 1};
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    font-size: 9px;
    padding: 1px 4px;
  }

  @media (max-width: 480px) {
    font-size: 8px;
    padding: 1px 2px;
  }
`;

const ClassTitle = styled.div`
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClassTime = styled.div`
  font-size: 9px;
  opacity: 0.9;
`;

const PastBadge = styled.div`
  font-size: 8px;
  font-weight: bold;
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.9);
`;

const monthNames = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

const weekDayNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const DetailRow = styled.div`
  margin-bottom: 10px;
  font-size: 14px;
  color: ${colors.text};
`;

const LocationLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.primaryDark};
    text-decoration: underline;
  }
`;

const weekDays = [
  { value: 0, label: 'Pazar' },
  { value: 1, label: 'Pazartesi' },
  { value: 2, label: 'Salı' },
  { value: 3, label: 'Çarşamba' },
  { value: 4, label: 'Perşembe' },
  { value: 5, label: 'Cuma' },
  { value: 6, label: 'Cumartesi' }
];

export const CalendarView = ({ classes = [], onClassUpdate, onClassDelete }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedClass, setSelectedClass] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [instructorPhoto, setInstructorPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Get start of week (Monday)
  const getStartOfWeek = (date) => {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day; // If Sunday, go back 6 days, else go to Monday
    const monday = new Date(date);
    monday.setDate(date.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  // Check if a class is in the past
  const isClassPast = (classDate, classTime) => {
    const now = new Date();
    const classDateTime = new Date(`${classDate}T${classTime || '00:00'}`);
    return classDateTime < now;
  };

  const getClassesForDate = (date) => {
    // Format date as YYYY-MM-DD in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return classes.filter(classItem => {
      if (classItem.isRecurring) {
        // Tekrarlı dersler için gün kontrolü ve başlangıç tarihi kontrolü
        const classDate = new Date(classItem.date + 'T00:00:00');
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        
        // Sadece ders başlangıç tarihinden sonraki aynı gün derslerini göster
        return classDate.getDay() === date.getDay() && checkDate >= classDate;
      } else {
        // Tek seferlik dersler için tarih kontrolü
        return classItem.date === dateStr;
      }
    });
  };

  const generateCalendarDays = () => {
    const startOfWeek = getStartOfWeek(currentDate);
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Generate 7 days starting from Monday
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      
      days.push({
        day: date.getDate(),
        date,
        isOtherMonth: false,
        isToday: date.toDateString() === today.toDateString(),
        classes: getClassesForDate(date)
      });
    }

    return days;
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get week date range for title
  const getWeekRange = () => {
    const startOfWeek = getStartOfWeek(currentDate);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const startDay = startOfWeek.getDate();
    const endDay = endOfWeek.getDate();
    const startMonth = monthNames[startOfWeek.getMonth()];
    const endMonth = monthNames[endOfWeek.getMonth()];
    const year = endOfWeek.getFullYear();

    if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
      return `${startDay} - ${endDay} ${startMonth} ${year}`;
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
    }
  };

  const handleClassClick = (classItem) => {
    setSelectedClass(classItem);
    setDetailModalOpen(true);
  };

  const handleEditClick = () => {
    setEditFormData({
      title: selectedClass.title,
      description: selectedClass.description,
      instructor: selectedClass.instructor,
      date: selectedClass.date,
      startTime: selectedClass.startTime || selectedClass.time || '',
      endTime: selectedClass.endTime || '',
      location: selectedClass.location || '',
      isOnline: selectedClass.isOnline || false,
      onlineLink: selectedClass.onlineLink || '',
      capacity: selectedClass.capacity,
      isRecurring: selectedClass.isRecurring,
      recurringDay: selectedClass.recurringDay || 1,
      isActive: selectedClass.isActive
    });
    setPhotoPreview(selectedClass.instructorPhoto || null);
    setDetailModalOpen(false);
    setEditModalOpen(true);
  };

  const handleDeleteClick = () => {
    if (window.confirm('Bu dersi silmek istediğinize emin misiniz?')) {
      if (onClassDelete) {
        onClassDelete(selectedClass.id);
      }
      setDetailModalOpen(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1048576) {
        alert('Fotoğraf boyutu 1MB\'dan küçük olmalıdır');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Sadece resim dosyaları yüklenebilir');
        return;
      }
      setInstructorPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitFormData = new FormData();
      Object.keys(editFormData).forEach(key => {
        submitFormData.append(key, editFormData[key]);
      });
      if (instructorPhoto) {
        submitFormData.append('instructorPhoto', instructorPhoto);
      }
      
      if (onClassUpdate) {
        await onClassUpdate(selectedClass.id, submitFormData);
      }
      setEditModalOpen(false);
    } catch (error) {
      alert(error.message || 'Güncelleme başarısız');
    }
  };

  const calendarDays = generateCalendarDays();

  return (
    <>
      <CalendarContainer>
      <CalendarHeader>
        <NavigationButton onClick={goToPreviousWeek}>
          ← Önceki Hafta
        </NavigationButton>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
          <MonthTitle>
            {getWeekRange()}
          </MonthTitle>
          <NavigationButton onClick={goToToday} style={{ fontSize: '12px', padding: '4px 8px' }}>
            Bugün
          </NavigationButton>
        </div>
        <NavigationButton onClick={goToNextWeek}>
          Sonraki Hafta →
        </NavigationButton>
      </CalendarHeader>

      <WeekDays>
        {weekDayNames.map(day => (
          <WeekDay key={day}>{day}</WeekDay>
        ))}
      </WeekDays>

      <CalendarGrid>
        {calendarDays.map((dayData, index) => (
          <CalendarDay
            key={index}
            isOtherMonth={dayData.isOtherMonth}
            isToday={dayData.isToday}
          >
            <DayNumber>{dayData.day}</DayNumber>
            <ClassList>
                {dayData.classes.map((classItem, classIndex) => {
                  // Format date as YYYY-MM-DD
                  const displayDate = `${dayData.date.getFullYear()}-${String(dayData.date.getMonth() + 1).padStart(2, '0')}-${String(dayData.date.getDate()).padStart(2, '0')}`;
                  const isPast = isClassPast(displayDate, classItem.startTime || classItem.time);
                  return (
                  <ClassItem
                    key={classIndex}
                    isRecurring={classItem.isRecurring}
                    $isPast={isPast}
                    onClick={() => handleClassClick(classItem)}
                    title={isPast ? `⏰ ${classItem.title} - Saati geçti` : `${classItem.title} - ${classItem.startTime}${classItem.endTime ? `-${classItem.endTime}` : ''}`}
                  >
                    <ClassTitle>{classItem.title}</ClassTitle>
                    <ClassTime>
                      {classItem.startTime}
                      {classItem.endTime ? `-${classItem.endTime}` : ''}
                    </ClassTime>
                    {isPast && <PastBadge>⏰ Geçti</PastBadge>}
                  </ClassItem>
                  );
                })}
            </ClassList>
          </CalendarDay>
        ))}
      </CalendarGrid>
    </CalendarContainer>

    {selectedClass && (
      <Modal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title="Ders Detayları"
      >
        <div style={{ marginBottom: '20px' }}>
          <DetailRow>
            <strong>📚 Ders:</strong> {selectedClass.title}
          </DetailRow>
          
          {selectedClass.description && (
            <DetailRow>
              <strong>📝 Açıklama:</strong> {selectedClass.description}
            </DetailRow>
          )}
          
          <DetailRow>
            <strong>👨‍🏫 Eğitmen:</strong> {selectedClass.instructor}
          </DetailRow>
          
          {selectedClass.isOnline ? (
            <DetailRow>
              <strong>💻 Online Ders</strong>
              {selectedClass.onlineLink && (
                <>
                  {' - '}
                  <LocationLink 
                    href={selectedClass.onlineLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Derse Katıl
                  </LocationLink>
                </>
              )}
            </DetailRow>
          ) : selectedClass.location && (
            <DetailRow>
              <strong>📍 Adres:</strong>{' '}
              <LocationLink 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedClass.location)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {selectedClass.location}
              </LocationLink>
            </DetailRow>
          )}
          
          <DetailRow>
            <strong>📅 Tarih:</strong> {selectedClass.date}
            {selectedClass.isRecurring && ' (Tekrarlı)'}
          </DetailRow>
          
          <DetailRow>
            <strong>🕐 Saat:</strong> {selectedClass.startTime || selectedClass.time}
            {selectedClass.endTime && ` - ${selectedClass.endTime}`}
          </DetailRow>
          
          <DetailRow>
            <strong>👥 Kontenjan:</strong> {selectedClass.currentBookings} / {selectedClass.capacity} Dolu
          </DetailRow>
          
          <DetailRow>
            <strong>🪑 Boş Yer:</strong> {selectedClass.availableSpots}
          </DetailRow>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <Button onClick={handleEditClick}>
            Düzenle
          </Button>
          <Button variant="danger" onClick={handleDeleteClick}>
            Sil
          </Button>
        </div>
      </Modal>
    )}

    {selectedClass && (
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Ders Düzenle"
        maxWidth="600px"
      >
        <form onSubmit={handleEditSubmit}>
          <FormGroup>
            <Label>Ders Başlığı</Label>
            <Input
              value={editFormData.title}
              onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
              required
              placeholder="örn: Sabah Yoga"
            />
          </FormGroup>

          <FormGroup>
            <Label>Açıklama (Opsiyonel)</Label>
            <TextArea
              value={editFormData.description}
              onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
              placeholder="Ders hakkında kısa bilgi..."
            />
          </FormGroup>

          <FormGroup>
            <Label>Eğitmen</Label>
            <Input
              value={editFormData.instructor}
              onChange={(e) => setEditFormData({...editFormData, instructor: e.target.value})}
              required
              placeholder="örn: Ahmet Yılmaz"
            />
          </FormGroup>

          <FormGroup>
            <Label>Eğitmen Fotoğrafı (Opsiyonel, Max 1MB)</Label>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ marginBottom: '10px' }}
            />
            {photoPreview && (
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <img 
                  src={photoPreview} 
                  alt="Eğitmen Önizleme" 
                  style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                />
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={editFormData.isOnline}
                onChange={(e) => setEditFormData({...editFormData, isOnline: e.target.checked, location: e.target.checked ? '' : 'Mutlukent Mah. 1951. Sok. No:18, Ankara, Turkey 06800'})}
                style={{ marginRight: '8px' }}
              />
              Online Ders
            </Label>
          </FormGroup>

          {editFormData.isOnline ? (
            <FormGroup>
              <Label>Online Ders Linki (Opsiyonel)</Label>
              <Input
                value={editFormData.onlineLink}
                onChange={(e) => setEditFormData({...editFormData, onlineLink: e.target.value})}
                placeholder="örn: https://zoom.us/j/123456789"
              />
            </FormGroup>
          ) : (
            <FormGroup>
              <Label>Adres</Label>
              <Input
                value={editFormData.location}
                onChange={(e) => setEditFormData({...editFormData, location: e.target.value})}
                placeholder="örn: Seed Yoga Studio, İstanbul"
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label>Tarih</Label>
            <Input
              type="date"
              value={editFormData.date}
              onChange={(e) => setEditFormData({...editFormData, date: e.target.value})}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Başlangıç Saati</Label>
            <Input
              type="time"
              value={editFormData.startTime}
              onChange={(e) => setEditFormData({...editFormData, startTime: e.target.value})}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Bitiş Saati (Opsiyonel)</Label>
            <Input
              type="time"
              value={editFormData.endTime}
              onChange={(e) => setEditFormData({...editFormData, endTime: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <Label>Kontenjan</Label>
            <Input
              type="number"
              min="1"
              value={editFormData.capacity}
              onChange={(e) => setEditFormData({...editFormData, capacity: parseInt(e.target.value)})}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={editFormData.isRecurring}
                onChange={(e) => setEditFormData({...editFormData, isRecurring: e.target.checked})}
                style={{ marginRight: '8px' }}
              />
              Tekrarlı Ders (Her hafta aynı gün)
            </Label>
          </FormGroup>

          {editFormData.isRecurring && (
            <FormGroup>
              <Label>Tekrar Günü</Label>
              <Select
                value={editFormData.recurringDay}
                onChange={(e) => setEditFormData({...editFormData, recurringDay: parseInt(e.target.value)})}
              >
                {weekDays.map(day => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </Select>
            </FormGroup>
          )}

          <FormGroup>
            <Label>Durum</Label>
            <Select
              value={editFormData.isActive}
              onChange={(e) => setEditFormData({...editFormData, isActive: e.target.value === 'true'})}
            >
              <option value="true">Aktif</option>
              <option value="false">Pasif</option>
            </Select>
          </FormGroup>

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <Button type="submit" style={{ flex: 1 }}>
              Güncelle
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditModalOpen(false)} style={{ flex: 1 }}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>
    )}
    </>
  );
};
